import React, { useState, useEffect } from "react";
import { useHR } from "../HRContext";
import { HRDepartment, DepartmentFormData, HREmployee } from "../../types/hr";
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Row,
  Col,
  Button,
  message,
  Spin,
} from "antd";

const { Option } = Select;
const { TextArea } = Input;

interface DepartmentFormProps {
  department?: HRDepartment | null;
  onClose: () => void;
}

const DepartmentForm: React.FC<DepartmentFormProps> = ({
  department,
  onClose,
}) => {
  const { employees, createDepartment, updateDepartment } = useHR();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (department) {
      form.setFieldsValue({
        name: department.name || "",
        code: department.code || "",
        description: department.description || "",
        manager_id: department.manager_id || undefined,
        parent_id: department.parent_id || undefined,
        budget_annual: department.budget_annual || undefined,
        is_active:
          department.is_active !== undefined ? department.is_active : true,
      });
    } else {
      form.resetFields();
    }
  }, [department, form]);

  const handleSubmit = async (values: DepartmentFormData) => {
    setLoading(true);
    try {
      if (department) {
        await updateDepartment(department.id, values);
        message.success("Département modifié avec succès");
      } else {
        await createDepartment(values);
        message.success("Département créé avec succès");
      }
      onClose();
    } catch (error) {
      message.error("Erreur lors de la sauvegarde du département");
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les employés actifs pour le choix du manager
  const availableManagers = employees.filter(
    (emp: HREmployee) => emp.status === "active"
  );

  return (
    <Modal
      title={department ? "Modifier le département" : "Nouveau département"}
      open={true}
      onCancel={onClose}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            is_active: true,
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Nom du département"
                rules={[
                  {
                    required: true,
                    message: "Le nom du département est requis",
                  },
                ]}
              >
                <Input placeholder="Ex: Ressources Humaines" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="code" label="Code du département">
                <Input placeholder="Ex: RH001" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea
              rows={3}
              placeholder="Description du département et de ses responsabilités..."
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="manager_id" label="Manager">
                <Select
                  placeholder="Sélectionner un manager"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.children || "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {availableManagers.map((emp: HREmployee) => (
                    <Option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item name="parent_id" label="Département parent">
                <Select
                  placeholder="Sélectionner un parent"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {/* TODO: Récupérer la liste des départements disponibles */}
                  <Option value={null}>Aucun parent</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item name="budget_annual" label="Budget annuel (FCFA)">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  formatter={(value) =>
                    value
                      ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                      : ""
                  }
                  placeholder="Budget annuel"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="is_active"
            label="Statut"
            rules={[{ required: true, message: "Le statut est requis" }]}
          >
            <Select>
              <Option value={true}>Actif</Option>
              <Option value={false}>Inactif</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
            >
              <Button onClick={onClose}>Annuler</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {department ? "Modifier" : "Créer"}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default DepartmentForm;
